import { AnimatedNumber } from './AnimatedNumber';
import { RulesDialog } from './RulesDialog';
import { lazy, Suspense, useState } from 'react';
import {
  ArrowUpRight,
  ArrowRight,
  Activity,
  Check,
  Trophy,
  X,
  UserRound,
} from 'lucide-react';
import type { Choice, OddsSelection } from '@pons/contracts';
import { Art, duration } from './arena-ui';
import { ArenaScreen } from './arena/ArenaScreen';
import { ExploreScreen } from './arena/ExploreScreen';
import { CoinModalProvider } from './arena/CoinModal';
import { exploreCoins, type ExploreFilter } from './arena/exploreModel';
import { TopBar } from './arena/TopBar';
import { InternalPanel, useInternalParams } from './arena/InternalPanel';
import { useSimulation } from './arena/useSimulation';
import { isCallable, type CoinView } from './arena/model';
import { useArena } from './useArena';
import { visibleRounds } from './market';
import { useAppNavigation } from './points-navigation';
import { PointsPage } from './reflex/PointsPage';
import { usePointsPreview } from './reflex/usePointsPreview';
import './reflex/points-host.css';
import { useGame } from './game/useGame';
import { HybridArt } from './game-preview/HybridArt';
import './game-preview/preview.css';
const MockArena = lazy(() => import('./demo/MockArena'));
export default function App() {
  const game = useGame();
  const [feed, setFeed] = useState<'live' | 'game'>('live');
  const {
    data,
    personal,
    leaders,
    now,
    ready,
    mode,
    notice,
    syncError,
    storageError,
    catchingUp,
    connection,
    marketStatus,
    setName,
    submit,
  } = useArena();
  const [view, setView] = useAppNavigation();
  const [rules, setRules] = useState(false);
  const points = usePointsPreview();
  const [exploreFilter, setExploreFilter] = useState<ExploreFilter>('Watching');
  const rounds = visibleRounds(data.rounds, now);
  const mine = personal;
  const finished = mine.filter((r) => r.outcome);
  const wins = finished.filter((r) => r.choice === r.outcome).length;
  const internal = useInternalParams();
  const simulation = useSimulation(
    internal.visible && internal.params.simulatedCoins,
    internal.params.simulatedTrading,
    now,
  );
  const coins = exploreCoins(rounds, personal, simulation.coins, now);
  const availablePredictions = {
    live:
      ready &&
      connection === 'connected' &&
      coins.some((coin) => isCallable(coin) && !coin.stale),
    game:
      game.ready && game.coins.some((coin) => isCallable(coin) && !coin.stale),
  };
  const explore = exploreCoins(data.rounds, personal, simulation.coins, now);
  const combined = [...coins, ...game.coins].sort((a, b) => b.start - a.start);
  const combinedExplore = [...explore, ...game.coins].sort(
    (a, b) => b.start - a.start,
  );
  const gameArt = new Map(game.rounds.map((round) => [round.id, round]));
  const roundById = new Map(
    [...data.rounds, ...personal].map((r) => [r.id, r]),
  );
  const artFor = (coin: CoinView, className?: string) => {
    const round = gameArt.get(coin.id);
    if (round)
      return round.hybrid ? (
        <HybridArt
          round={{ seed: round.visualSeed, image: round.image }}
          name={coin.name}
          className={className}
        />
      ) : (
        <img
          className={`token-art ${className || ''}`}
          src={round.image}
          alt={`${coin.name} · Game token`}
        />
      );
    return coin.simulated ? (
      simulation.art(coin, className)
    ) : (
      <Art
        symbol={coin.symbol}
        r={roundById.get(coin.id)}
        className={className}
      />
    );
  };
  const predict = (id: string, choice: Choice, odds?: OddsSelection) => {
    if (id.startsWith('game-')) return game.vote(id, choice);
    return id.startsWith('sim-')
      ? simulation.call(id, choice)
      : submit(id, choice, odds);
  };
  const status =
    connection === 'connected'
      ? marketStatus === 'live'
        ? ({ tone: 'live', label: 'Live connection' } as const)
        : ({ tone: 'warn', label: 'Market recovering' } as const)
      : ({ tone: 'down', label: 'Reconnecting' } as const);
  const ranking = leaders.map((p) => (p.you ? { ...p, name: data.name } : p));
  if (mode === 'mock')
    return (
      <Suspense fallback={<div>Loading demo…</div>}>
        <MockArena />
      </Suspense>
    );
  const page = (
    <main className={'arena-app' + (view === 'Arena' ? ' has-arena' : '')}>
      <TopBar
        view={view}
        status={status}
        previewBalance={points.state.connected ? points.formattedBalance : '0'}
        onView={setView}
        onProfile={() => setView('Profile')}
      />
      {view === 'Arena' && (
        <div className="game-toolbar">
          <div
            className="game-switch"
            role="group"
            aria-label="Filter launches"
          >
            {(['live', 'game'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={feed === value}
                aria-label={`${value === 'live' ? 'Live' : 'Game'}${availablePredictions[value] ? ' — prediction available' : ''}`}
                title={
                  availablePredictions[value]
                    ? 'Open prediction available'
                    : undefined
                }
                onClick={() => setFeed(value)}
              >
                {value === 'live' ? 'Live' : 'Game'}
                {availablePredictions[value] && (
                  <span className="game-switch-dot" aria-hidden="true" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="app-notices">
        {game.error && (feed === 'game' || view === 'Explore') && (
          <p role="status" className="storage-error">
            {game.error}
          </p>
        )}
        {syncError && !(view === 'Arena' && feed === 'game') && (
          <p role="status" className="storage-error">
            {syncError}
          </p>
        )}
        {storageError && (
          <p role="alert" className="storage-error">
            Your display name cannot be saved in this browser. Predictions are
            saved on the server.
          </p>
        )}
      </div>
      {notice && (
        <div className="toast" role="status">
          <Check size={18} />
          {notice}
        </div>
      )}
      {view === 'Points' ? (
        <div className="points-host">
          <PointsPage
            preview={points}
            environment="live"
            goToArena={() => setView('Arena')}
          />
        </div>
      ) : view === 'Arena' ? (
        <ArenaScreen
          coins={feed === 'game' ? game.coins : coins}
          sidebarCoins={combined}
          now={feed === 'game' ? game.now : now}
          ready={feed === 'game' ? game.ready : ready}
          art={artFor}
          onPredict={predict}
          onExplore={() => {
            setExploreFilter('Watching');
            setView('Explore');
          }}
          onPredictions={() => {
            setExploreFilter('My bets');
            setView('Explore');
          }}
          motion={internal.params.queue}
          railMotion={internal.params.rails}
        />
      ) : view === 'Explore' ? (
        <ExploreScreen
          coins={combinedExplore}
          sidebarCoins={combinedExplore}
          now={now}
          ready={ready}
          art={artFor}
          filter={exploreFilter}
          onFilter={setExploreFilter}
          onArena={() => setView('Arena')}
          catchingUp={catchingUp}
          motion={internal.params.queue}
          railMotion={internal.params.rails}
        />
      ) : (
        <div className="app-shell">
          <section className="inner-page">
            <span className="eyebrow">RUN OR DIE / SURVIVAL CLUB</span>
            <h1>{view}</h1>
            {view === 'My predictions' && (
              <>
                <div className="stat-grid">
                  <article>
                    <span>TOTAL CALLS</span>
                    <b>
                      <AnimatedNumber value={mine.length} />
                    </b>
                  </article>
                  <article>
                    <span>WAITING FOR RESULTS</span>
                    <b>
                      <AnimatedNumber value={mine.length - finished.length} />
                    </b>
                  </article>
                  <article>
                    <span>CORRECT CALLS</span>
                    <b className="positive">
                      <AnimatedNumber value={wins} />
                    </b>
                  </article>
                </div>
                {!mine.length ? (
                  <div className="empty">
                    <Activity />
                    <h2>Your first call starts here.</h2>
                    <p>Choose a token in the arena. Pick RUN! or DIE!.</p>
                    <button
                      className="primary"
                      onClick={() => setView('Arena')}
                    >
                      Enter the arena <ArrowUpRight size={17} />
                    </button>
                  </div>
                ) : (
                  <div className="prediction-list">
                    {mine.map((r) => (
                      <div className="prediction-row" key={r.id}>
                        <Art symbol={r.symbol} r={r} />
                        <div>
                          <h3>{r.name}</h3>
                          <small>
                            ${r.symbol} / {new Date(r.start).toLocaleString()}
                          </small>
                        </div>
                        <span
                          className={
                            r.choice === 'SURVIVES' ? 'positive' : 'negative'
                          }
                        >
                          {r.choice === 'SURVIVES' ? 'RUN!' : 'DIE!'}
                        </span>
                        <b>
                          <AnimatedNumber
                            value={
                              r.canonical === false
                                ? 'Rechecking chain data'
                                : !r.outcome
                                  ? now < r.settlesAt
                                    ? 'Result in ' + duration(r.settlesAt - now)
                                    : 'Pending verified data'
                                  : r.choice === r.outcome
                                    ? 'Correct / +100 pts'
                                    : 'Incorrect / +0 pts'
                            }
                          />
                        </b>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {view === 'Leaderboard' && (
              <>
                <p className="page-description">
                  The calls that counted. 100 points for every correct
                  prediction.
                </p>
                {!ranking.length && (
                  <div className="empty">
                    <Trophy />
                    <h2>The first place is still open.</h2>
                    <p>
                      Make a prediction. Correct settled calls earn 100 points.
                    </p>
                    <button
                      className="primary"
                      onClick={() => setView('Arena')}
                    >
                      Back to the arena <ArrowUpRight size={16} />
                    </button>
                  </div>
                )}
                <div className="leaderboard">
                  {ranking.map((p, i) => (
                    <div
                      className={'leader-row ' + (p.you ? 'is-you' : '')}
                      key={p.id}
                    >
                      <span className="rank">
                        <AnimatedNumber
                          value={String(i + 1).padStart(2, '0')}
                        />
                      </span>
                      <span className="leader-avatar">{p.avatar}</span>
                      <div>
                        <h3>
                          {p.name} {p.you && <small>YOU</small>}
                        </h3>
                        <span>
                          {p.you ? 'Your profile' : 'Anonymous predictor'}
                        </span>
                      </div>
                      <b>
                        <AnimatedNumber value={p.score.toLocaleString()} />{' '}
                        <small>PTS</small>
                      </b>
                    </div>
                  ))}
                </div>
                <p className="fineprint">
                  Scores come from verified settled calls. Anonymous profiles
                  are tied to their browser session.
                </p>
              </>
            )}
            {view === 'Profile' && (
              <div className="profile-grid">
                <article className="profile-panel">
                  <UserRound size={40} />
                  <h2>Your survival identity.</h2>
                  <label>
                    Display name
                    <input
                      value={data.name}
                      maxLength={30}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </label>
                  <p>Saved in this browser automatically.</p>
                  {game.stats && (
                    <p>
                      Game: {game.stats.predictions} predictions ·{' '}
                      {game.stats.wins} correct · {game.stats.points} game
                      points
                    </p>
                  )}
                </article>
                <div>
                  <div className="stat-grid">
                    <article>
                      <span>YOUR POINTS</span>
                      <b>
                        <AnimatedNumber value={wins * 100} />
                      </b>
                    </article>
                    <article>
                      <span>ACCURACY</span>
                      <b>
                        <AnimatedNumber
                          value={
                            finished.length
                              ? Math.round((wins / finished.length) * 100) + '%'
                              : '—'
                          }
                        />
                      </b>
                    </article>
                  </div>
                </div>
              </div>
            )}
          </section>
          <footer className="site-footer">
            <span className="footer-brand">pons.</span>
            <p>
              Good instincts.
              <br />
              Better calls.
            </p>
            <span>
              PONS V2 / ROBINHOOD CHAIN
              <br />
              On-chain tokens / Predictions saved on the server
            </span>
            <button onClick={() => setRules(true)}>
              The rules <ArrowUpRight size={16} />
            </button>
          </footer>
        </div>
      )}
      {rules && (
        <RulesDialog onClose={() => setRules(false)}>
          <button
            className="close"
            aria-label="Close rules"
            onClick={() => setRules(false)}
          >
            <X />
          </button>
          <span className="eyebrow">NO STAKES. JUST INSTINCT.</span>
          <h2 id="rules-title">
            Three steps.
            <br />
            One good call.
          </h2>
          <ol>
            <li>
              <b>Spot the graduation.</b>
              <p>
                A token enters the arena when Pons V2 creates its graduated
                pool.
              </p>
            </li>
            <li>
              <b>Make your call in 20 seconds.</b>
              <p>
                Choose RUN! or DIE!. Your choice locks immediately and reveals
                the community split.
              </p>
            </li>
            <li>
              <b>See what survived.</b>
              <p>
                After 5 minutes: at least 50% USD price retention and one
                external pool swap means RUN!. Correct calls earn 100 points.
              </p>
            </li>
          </ol>
          <p className="fineprint">
            New rounds use USD prices; earlier rounds keep their original
            pair-based rules. Missing historical USD prices or incomplete
            activity leave the result pending. Predictions are saved on the
            server for this browser profile.
          </p>
          <button className="primary" onClick={() => setRules(false)}>
            Got it. Let&apos;s play <ArrowRight size={16} />
          </button>
        </RulesDialog>
      )}
      {internal.visible && (
        <InternalPanel
          params={internal.params}
          onChange={internal.setParams}
          simulatedCount={simulation.coins.length}
          onCreateToken={(name, symbol) => {
            simulation.create(name, symbol);
          }}
          onClearAll={simulation.clear}
        />
      )}
    </main>
  );
  return (
    <CoinModalProvider coins={combinedExplore} now={now} art={artFor}>
      {page}
    </CoinModalProvider>
  );
}
